import * as XLSX from 'xlsx';

export const readExcelFile = async (filePath) => {
  try {
    const response = await fetch(filePath);
    const arrayBuffer = await response.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });

    const worksheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[worksheetName];

    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    return jsonData;
  } catch (error) {
    console.error('Excel dosyası okuma hatası:', error);
    return [];
  }
};

export const processStatisticsData = (rawData) => {
  if (!rawData || rawData.length === 0) return null;

  const headers = rawData[0];
  const dataRows = rawData.slice(1).filter(row => row.length > 0);

  console.log('Excel Headers:', headers);
  console.log('Data Rows Sample:', dataRows.slice(0, 5));

  // Excel sütunlarını analiz et
  const analysis = {
    totalResponses: dataRows.length,
    columnAnalysis: analyzeColumns(headers, dataRows),
    scoreDistribution: calculateScoreDistribution(dataRows, headers),
    categoryBreakdown: analyzeCategoryData(dataRows, headers),
    timeAnalysis: analyzeTimeData(dataRows, headers),
    demographicData: analyzeDemographics(dataRows, headers),
    questionAnalysis: analyzeQuestionDifficulty(dataRows, headers),
    improvementTrends: analyzeImprovementTrends(dataRows, headers)
  };

  return analysis;
};

const analyzeColumns = (headers, dataRows) => {
  const columnStats = {};

  headers.forEach((header, index) => {
    if (!header) return;

    const columnData = dataRows.map(row => row[index]).filter(val => val !== undefined && val !== '');
    const numericData = columnData.filter(val => !isNaN(val) && val !== '').map(Number);

    columnStats[header] = {
      totalValues: columnData.length,
      uniqueValues: [...new Set(columnData)].length,
      isNumeric: numericData.length > columnData.length * 0.7,
      average: numericData.length > 0 ? numericData.reduce((a, b) => a + b, 0) / numericData.length : 0,
      min: numericData.length > 0 ? Math.min(...numericData) : 0,
      max: numericData.length > 0 ? Math.max(...numericData) : 0,
      sampleValues: columnData.slice(0, 5)
    };
  });

  return columnStats;
};

const calculateScoreDistribution = (dataRows, headers) => {
  // Puan ile ilgili sütunları bul
  const scoreColumns = headers.filter(h =>
    h && (h.toLowerCase().includes('score') ||
          h.toLowerCase().includes('puan') ||
          h.toLowerCase().includes('point') ||
          h.toLowerCase().includes('doğru') ||
          h.toLowerCase().includes('yanlış'))
  );

  const distribution = {};

  scoreColumns.forEach(column => {
    const columnIndex = headers.indexOf(column);
    const scores = dataRows
      .map(row => row[columnIndex])
      .filter(score => !isNaN(score) && score !== '')
      .map(Number);

    if (scores.length > 0) {
      // 0-20, 21-40, 41-60, 61-80, 81-100 aralıkları
      const ranges = {
        '0-20': 0, '21-40': 0, '41-60': 0, '61-80': 0, '81-100': 0
      };

      scores.forEach(score => {
        if (score <= 20) ranges['0-20']++;
        else if (score <= 40) ranges['21-40']++;
        else if (score <= 60) ranges['41-60']++;
        else if (score <= 80) ranges['61-80']++;
        else ranges['81-100']++;
      });

      distribution[column] = {
        ranges,
        average: scores.reduce((a, b) => a + b, 0) / scores.length,
        median: scores.sort((a, b) => a - b)[Math.floor(scores.length / 2)],
        total: scores.length
      };
    }
  });

  return distribution;
};

const analyzeCategoryData = (dataRows, headers) => {
  // Kategori ile ilgili sütunları bul
  const categoryColumns = headers.filter(h =>
    h && (h.toLowerCase().includes('kategori') ||
          h.toLowerCase().includes('category') ||
          h.toLowerCase().includes('konu') ||
          h.toLowerCase().includes('alan'))
  );

  const categoryAnalysis = {};

  categoryColumns.forEach(column => {
    const columnIndex = headers.indexOf(column);
    const categories = dataRows
      .map(row => row[columnIndex])
      .filter(cat => cat && cat !== '');

    const categoryCount = {};
    categories.forEach(cat => {
      categoryCount[cat] = (categoryCount[cat] || 0) + 1;
    });

    categoryAnalysis[column] = categoryCount;
  });

  return categoryAnalysis;
};

const analyzeTimeData = (dataRows, headers) => {
  // Zaman ile ilgili sütunları bul
  const timeColumns = headers.filter(h =>
    h && (h.toLowerCase().includes('time') ||
          h.toLowerCase().includes('zaman') ||
          h.toLowerCase().includes('date') ||
          h.toLowerCase().includes('tarih'))
  );

  const timeAnalysis = {};

  timeColumns.forEach(column => {
    const columnIndex = headers.indexOf(column);
    const timeData = dataRows
      .map(row => row[columnIndex])
      .filter(time => time && time !== '');

    // Zaman verilerini analiz et
    const timeStats = {
      totalEntries: timeData.length,
      uniqueDates: [...new Set(timeData)].length,
      sampleData: timeData.slice(0, 10)
    };

    timeAnalysis[column] = timeStats;
  });

  return timeAnalysis;
};

const analyzeDemographics = (dataRows, headers) => {
  // Demografik veriler için sütunları bul
  const demoColumns = headers.filter(h =>
    h && (h.toLowerCase().includes('yaş') ||
          h.toLowerCase().includes('age') ||
          h.toLowerCase().includes('cinsiyet') ||
          h.toLowerCase().includes('gender') ||
          h.toLowerCase().includes('şehir') ||
          h.toLowerCase().includes('city'))
  );

  const demographics = {};

  demoColumns.forEach(column => {
    const columnIndex = headers.indexOf(column);
    const demoData = dataRows
      .map(row => row[columnIndex])
      .filter(data => data && data !== '');

    const demoCount = {};
    demoData.forEach(item => {
      demoCount[item] = (demoCount[item] || 0) + 1;
    });

    demographics[column] = demoCount;
  });

  return demographics;
};

const analyzeQuestionDifficulty = (dataRows, headers) => {
  // Soru zorluk analizi
  const questionColumns = headers.filter(h =>
    h && (h.toLowerCase().includes('soru') ||
          h.toLowerCase().includes('question') ||
          h.toLowerCase().includes('q'))
  );

  const difficultyAnalysis = {};

  questionColumns.forEach(column => {
    const columnIndex = headers.indexOf(column);
    const answers = dataRows
      .map(row => row[columnIndex])
      .filter(answer => answer !== undefined && answer !== '');

    // Doğru/yanlış oranlarını hesapla
    const correctCount = answers.filter(answer =>
      answer === 1 || answer === '1' ||
      answer === 'doğru' || answer === 'Doğru' ||
      answer === 'true' || answer === 'True'
    ).length;

    const difficultyLevel = correctCount / answers.length;
    let difficulty = 'Orta';
    if (difficultyLevel > 0.8) difficulty = 'Kolay';
    else if (difficultyLevel < 0.4) difficulty = 'Zor';

    difficultyAnalysis[column] = {
      correctRate: difficultyLevel,
      difficulty,
      totalResponses: answers.length,
      correctCount
    };
  });

  return difficultyAnalysis;
};

const analyzeImprovementTrends = (dataRows, headers) => {
  // Gelişim trendi analizi (öntest-sontest karşılaştırması)
  const preTestColumns = headers.filter(h =>
    h && (h.toLowerCase().includes('ön') ||
          h.toLowerCase().includes('pre') ||
          h.toLowerCase().includes('before'))
  );

  const postTestColumns = headers.filter(h =>
    h && (h.toLowerCase().includes('son') ||
          h.toLowerCase().includes('post') ||
          h.toLowerCase().includes('after'))
  );

  const trends = {
    preTest: {},
    postTest: {},
    improvement: {}
  };

  // Pre-test analizi
  preTestColumns.forEach(column => {
    const columnIndex = headers.indexOf(column);
    const scores = dataRows
      .map(row => row[columnIndex])
      .filter(score => !isNaN(score) && score !== '')
      .map(Number);

    if (scores.length > 0) {
      trends.preTest[column] = {
        average: scores.reduce((a, b) => a + b, 0) / scores.length,
        count: scores.length
      };
    }
  });

  // Post-test analizi
  postTestColumns.forEach(column => {
    const columnIndex = headers.indexOf(column);
    const scores = dataRows
      .map(row => row[columnIndex])
      .filter(score => !isNaN(score) && score !== '')
      .map(Number);

    if (scores.length > 0) {
      trends.postTest[column] = {
        average: scores.reduce((a, b) => a + b, 0) / scores.length,
        count: scores.length
      };
    }
  });

  return trends;
};
